// Dinheiro digitado e dinheiro mostrado.
//
// Victor, 10/09/2026: "a forma como aparece valor é ruim, tipo fica 600,0,
// quero que fique tipo R$ 600,00, formatado certo".
//
// 🔴 O CAMPO GUARDA TEXTO, E O PAYLOAD PRECISA DE NÚMERO.
//
// Por isso a dupla: `formatarMoeda` é o que a pessoa lê, `numeroDeMoeda` é o
// que vai para o servidor. Formatar sem ter o caminho de volta é como se
// perde valor — "1.234,50" vira 1.0 num `parseFloat` desavisado, e ninguém
// percebe até a cobrança sair errada.

/**
 * Texto do campo -> número.
 *
 * 🔴 R$ 1.357,20 VIRAVA R$ 1,36 (Victor, 11/09/2026).
 *
 * "A Dra. Jullya tá tendo muita dificuldade em colocar o valor aqui, ela tá
 * colocando tipo um valor de 1357,30 reais e aí quando salva salva tipo 1,37,
 * errado. Isso tem atrapalhado o dia a dia, tem convertido valores para
 * outros."
 *
 * A CAUSA, medida: a versão anterior elegia como decimal o separador que
 * aparecesse por último. Num texto com PONTO SOZINHO isso dava o ponto — e
 * "1.357" era lido como um real e trezentos e cinquenta e sete milésimos, que
 * arredondado mostra 1,36. Os dois movimentos de R$ 1,36 no print dele são
 * exatamente isso. Também errava "1.000" (virava 1,00) e "10.000" (10,00).
 * Erro de fator MIL em cima de dinheiro de cliente.
 *
 * A REGRA AGORA, e por quê:
 *
 *   1. Tem vírgula? A vírgula é o decimal, e todo ponto é milhar. É a
 *      convenção brasileira, e quem digita aqui é advogado brasileiro.
 *      "1.357,20" e "1.357.200,50" caem aqui.
 *
 *   2. Só ponto, mais de um? São milhares. "1.357.200".
 *
 *   3. Só ponto, um, com EXATAMENTE 3 dígitos depois? É milhar — decisão do
 *      Victor: "se colocar 1.370 é mil trezentos e setenta". Grupo de três é
 *      grupo, e é assim que separador de milhar se reconhece: ele aparece em
 *      posição previsível, de três em três.
 *
 *   4. Só ponto, um, com 1, 2 ou mais de 3 dígitos depois? É decimal. Cobre o
 *      que a própria API devolve ("1357.2", "1357.20") e quem digita no
 *      padrão do sistema.
 *
 * Seguro contra o caso do item 3 comer casas decimais porque as colunas de
 * dinheiro do banco são `decimal(_, 2)` — a API nunca manda três casas.
 *
 * Aceita: "1,37" • "1370" • "1.370" • "1370,30" • "1.357,20" • "1357.20"
 *         • "1.357.200,50" • "R$ 90" • "-1.234,50"
 */
export const numeroDeMoeda = texto => {
  if (typeof texto === 'number') return texto;

  const limpo = String(texto ?? '').replace(/[^\d,.-]/g, '');
  if (!limpo) return null;

  const negativo = limpo.trimStart().startsWith('-');
  const semSinal = limpo.replace(/-/g, '');

  const digitosDepois = separador => {
    const ultimo = semSinal.lastIndexOf(separador);
    return ultimo === -1 ? -1 : semSinal.length - ultimo - 1;
  };

  const pontos = (semSinal.match(/\./g) || []).length;
  const temVirgula = semSinal.includes(',');

  let inteiro = semSinal;
  let fracao = '';

  if (temVirgula) {
    // (1) Convenção brasileira: vírgula decide, ponto é milhar.
    const corte = semSinal.lastIndexOf(',');
    inteiro = semSinal.slice(0, corte);
    fracao = semSinal.slice(corte + 1);
  } else if (pontos === 1 && digitosDepois('.') !== 3) {
    // (4) Um ponto que não fecha grupo de três: é decimal.
    const corte = semSinal.lastIndexOf('.');
    inteiro = semSinal.slice(0, corte);
    fracao = semSinal.slice(corte + 1);
  }
  // (2) e (3) caem aqui: todo ponto é milhar, e some na limpeza abaixo.

  const digitosInteiros = inteiro.replace(/\D/g, '');
  const digitosFracao = fracao.replace(/\D/g, '');

  const numero = Number(`${digitosInteiros || '0'}.${digitosFracao || '0'}`);
  if (!Number.isFinite(numero)) return null;
  return negativo ? -numero : numero;
};

/** Número -> "1.234,50". Sem o "R$": ele é adorno do campo, não conteúdo. */
export const formatarMoeda = valor => {
  const numero = numeroDeMoeda(valor);
  if (numero === null) return '';

  return numero.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** Com o símbolo, para texto corrido. */
export const moedaComSimbolo = valor => {
  const texto = formatarMoeda(valor);
  return texto ? `R$ ${texto}` : '';
};
